import { For, Match, Show, Switch, createSignal } from "solid-js";
import type { Component } from "solid-js";
import { DreameIntegrationView } from "./DreameIntegrationView";

type SettingsCategory = "payments" | "robots" | "locations" | "notifications" | "general";
type RobotsSubcategory = "dreame" | "roborock" | "ecovacs";

interface SettingsNavItem {
  id: string;
  label: string;
  icon?: string;
  type: "category" | "subcategory";
  parent?: string;
}

const settingsStructure: SettingsNavItem[] = [
  { id: "general", label: "General", icon: "fa-cog", type: "category" },
  { id: "payments", label: "Payments", icon: "fa-credit-card", type: "category" },
  { id: "robots", label: "Robots", icon: "fa-robot", type: "category" },
  { id: "robots-dreame", label: "Dreame Cloud", type: "subcategory", parent: "robots" },
  { id: "robots-roborock", label: "Roborock", type: "subcategory", parent: "robots" },
  { id: "robots-ecovacs", label: "Ecovacs", type: "subcategory", parent: "robots" },
  { id: "locations", label: "Locations", icon: "fa-map-marker", type: "category" },
  { id: "notifications", label: "Notifications", icon: "fa-bell", type: "category" },
];

export const SettingsMain: Component = () => {
  const [activeCategory, setActiveCategory] = createSignal<string>("general");
  const [activeSubcategory, setActiveSubcategory] = createSignal<string>("");
  const [expandedCategories, setExpandedCategories] = createSignal<Set<string>>(new Set());

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleNavClick = (item: SettingsNavItem) => {
    if (item.type === "category") {
      const hasSubcategories = settingsStructure.some(i => i.parent === item.id);
      if (hasSubcategories) {
        toggleCategory(item.id);
      } else {
        setActiveCategory(item.id);
        setActiveSubcategory("");
      }
    } else {
      setActiveCategory(item.parent!);
      setActiveSubcategory(item.id);
    }
  };

  const categories = () => settingsStructure.filter(item => item.type === "category");
  const getSubcategories = (categoryId: string) =>
    settingsStructure.filter(item => item.parent === categoryId);

  const isActive = (item: SettingsNavItem) => {
    if (item.type === "subcategory") {
      return activeSubcategory() === item.id;
    }
    return activeCategory() === item.id && !activeSubcategory();
  };

  return (
    <div class="settings-container">
      <header class="chat-list-header">
        <div>
          <h1 class="main-placeholder-title">Settings</h1>
          <p>Configure integrations, manage system preferences, and control access permissions.</p>
        </div>
      </header>

      {/* Horizontal navigation for all screen sizes */}
      <nav class="settings-horizontal-nav">
        <div class="horizontal-tabs-container">
          <For each={categories()}>
            {(category) => {
              const subcategories = () => getSubcategories(category.id);
              const hasSubcategories = subcategories().length > 0;
              const isExpanded = () => expandedCategories().has(category.id);

              return (
                <div
                  class={`horizontal-tab-item ${isActive(category) ? "active" : ""}`}
                  onClick={() => handleNavClick(category)}
                >
                  <Show when={category.icon}>
                    <i class={`fas ${category.icon}`} style="font-size: 0.875rem;"></i>
                  </Show>
                  <span>{category.label}</span>
                  <Show when={hasSubcategories}>
                    <i class={`fas fa-chevron-${isExpanded() ? "up" : "down"}`} style="margin-left: 4px; font-size: 0.75rem;"></i>
                  </Show>
                </div>
              );
            }}
          </For>
        </div>

        {/* Dropdown menu for subcategories */}
        <Show when={expandedCategories().has("robots")}>
          <div class="settings-nav-dropdown-overlay">
            <For each={getSubcategories("robots")}>
              {(subcategory) => (
                <button
                  class={`settings-nav-dropdown-item ${isActive(subcategory) ? "active" : ""}`}
                  onClick={() => handleNavClick(subcategory)}
                >
                  {subcategory.label}
                </button>
              )}
            </For>
          </div>
        </Show>
      </nav>

      <div class="settings-layout">
        <div class="settings-content">
          <Switch fallback={<SettingsPlaceholder category={activeCategory()} />}>
            <Match when={activeSubcategory() === "robots-dreame"}>
              <DreameIntegrationView />
            </Match>
            <Match when={activeCategory() === "payments" && !activeSubcategory()}>
              <PaymentsSettingsView />
            </Match>
            <Match when={activeCategory() === "locations" && !activeSubcategory()}>
              <LocationsSettingsView />
            </Match>
            <Match when={activeCategory() === "notifications" && !activeSubcategory()}>
              <NotificationsSettingsView />
            </Match>
            <Match when={activeCategory() === "general" && !activeSubcategory()}>
              <GeneralSettingsView />
            </Match>
          </Switch>
        </div>
      </div>
    </div>
  );
};

const SettingsPlaceholder: Component<{ category: string }> = (props) => (
  <div class="settings-placeholder">
    <h2>{props.category.charAt(0).toUpperCase() + props.category.slice(1)} Settings</h2>
    <p>This settings section is coming soon.</p>
  </div>
);

const PaymentsSettingsView: Component = () => (
  <section class="settings-view">
    <h2>Payment Settings</h2>
    <div class="settings-card">
      <div class="settings-card-header">
        <div>
          <h3>Stripe Configuration</h3>
          <p>Configure Stripe payment processing integration.</p>
        </div>
      </div>
      <div class="settings-coming-soon">
        <i class="fas fa-credit-card"></i>
        <p>Stripe payment settings will be available here soon.</p>
      </div>
    </div>
  </section>
);

const LocationsSettingsView: Component = () => (
  <section class="settings-view">
    <h2>Location Settings</h2>
    <div class="settings-card">
      <div class="settings-card-header">
        <div>
          <h3>Service Areas</h3>
          <p>Manage serviceable locations and building configurations.</p>
        </div>
      </div>
      <div class="settings-coming-soon">
        <i class="fas fa-map-marker"></i>
        <p>Location management settings will be available here soon.</p>
      </div>
    </div>
  </section>
);

const NotificationsSettingsView: Component = () => (
  <section class="settings-view">
    <h2>Notification Settings</h2>
    <div class="settings-card">
      <div class="settings-card-header">
        <div>
          <h3>Notification Channels</h3>
          <p>Configure SMS, email, and in-app notification preferences.</p>
        </div>
      </div>
      <div class="settings-coming-soon">
        <i class="fas fa-bell"></i>
        <p>Notification settings will be available here soon.</p>
      </div>
    </div>
  </section>
);

const GeneralSettingsView: Component = () => (
  <section class="settings-view">
    <h2>General Settings</h2>
    <div class="settings-card">
      <div class="settings-card-header">
        <div>
          <h3>System Preferences</h3>
          <p>Configure general system settings and preferences.</p>
        </div>
      </div>
      <div class="settings-coming-soon">
        <i class="fas fa-cog"></i>
        <p>General settings will be available here soon.</p>
      </div>
    </div>
  </section>
);